import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, Badge } from '@tremor/react';
import { KPICard } from '@/components/KPICard';
import { getUnitEconomics, extractErrorMessage } from '@/lib/api';

// ── Tipos (payload de GET /unit-economics?days=N) ───────────────────────────

interface CanalRow {
  canal: string;
  spend: number | null;
  spend_status: string;
  novas_orgs: number;
  novas_orgs_pagantes: number;
  receita: number;
  cac: number | null;
  cac_pago: number | null;
  payback_cac_sobre_ltv: number | null;
  roas: number | null;
  conversao_pagante: number | null;
}

interface LtvGlobal {
  receita_total: number;
  n_orgs_pagantes: number;
  n_compras: number;
  ltv_medio: number | null;
}

interface Totais {
  gasto: number | null;
  novas_orgs: number;
  novas_orgs_pagantes: number;
  receita_periodo: number;
  cac: number | null;
  cac_pago: number | null;
  roas: number | null;
}

interface UnitEconomicsData {
  disponivel: boolean;
  erro?: string;
  days: number;
  since: string;
  until: string;
  ltv_global: LtvGlobal;
  canais: CanalRow[];
  totais: Totais;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const PERIODOS = [7, 14, 30];

function fmtBRL(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtX(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(2)}×`;
}

function canalColor(canal: string): 'blue' | 'violet' | 'slate' {
  if (canal === 'Meta') return 'blue';
  if (canal === 'Google') return 'violet';
  return 'slate';
}

export function UnitEconomicsPage() {
  const [days, setDays] = useState(30);

  const { data, isLoading, error } = useQuery<UnitEconomicsData>({
    queryKey: ['unit-economics', days],
    queryFn: async () => (await getUnitEconomics(days)).data,
    refetchInterval: 5 * 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar unit economics')
    : data && !data.disponivel
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const t = data?.totais;
  const ltv = data?.ltv_global;
  const canais = data?.canais ?? [];

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <Title>Unit Economics</Title>
          <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDays(p)}
                className={`rounded px-2.5 py-1 text-xs transition ${
                  days === p
                    ? 'bg-blue-600/30 text-blue-200'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
        <Text>
          CAC / LTV / payback por canal — decidir acelerar vs segurar ads por número.
          Gasto via Meta + Google Ads API; receita só Stripe (compra real). LTV é
          histórico completo (não recortado pelo período selecionado acima).
        </Text>
        {data?.since && data?.until && (
          <Text className="text-xs text-slate-500">
            Período de gasto/novas-orgs: {data.since} a {data.until}
          </Text>
        )}
      </header>

      {!isLoading && errMsg && (
        <Card className="border border-red-800/40 bg-red-900/10">
          <Text className="text-red-300">{errMsg}</Text>
        </Card>
      )}

      {/* LTV global + totais do período */}
      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        <KPICard
          label="LTV médio/org pagante (histórico)"
          value={isLoading ? null : fmtBRL(ltv?.ltv_medio)}
          loading={isLoading}
          error={errMsg}
          hint={`${fmtInt(ltv?.n_orgs_pagantes)} orgs pagantes · ${fmtInt(ltv?.n_compras)} compras · receita total ${fmtBRL(ltv?.receita_total)}`}
        />
        <KPICard
          label={`Gasto total (${days}d)`}
          value={isLoading ? null : t?.gasto === null ? 'indisponível' : fmtBRL(t?.gasto)}
          loading={isLoading}
          error={errMsg}
          hint="soma dos canais com pull de gasto OK (Meta + Google)"
        />
        <KPICard
          label={`CAC-pago (${days}d)`}
          value={isLoading ? null : fmtBRL(t?.cac_pago)}
          loading={isLoading}
          error={errMsg}
          hint={`gasto / ${fmtInt(t?.novas_orgs_pagantes)} novas orgs pagantes`}
        />
        <KPICard
          label={`ROAS (${days}d)`}
          value={isLoading ? null : t?.roas === null ? '—' : `${t?.roas?.toFixed(2)}×`}
          loading={isLoading}
          error={errMsg}
          hint={`receita ${fmtBRL(t?.receita_periodo)} / gasto ${fmtBRL(t?.gasto)}`}
        />
      </Grid>

      {/* Tabela por canal */}
      <Card>
        <Title>Economics por canal</Title>
        <Text className="text-xs text-slate-400">
          CAC = gasto / novas orgs. CAC-pago = gasto / novas orgs que já converteram.
          Payback = CAC-pago / LTV (em múltiplos de LTV — abaixo de 1× é bom sinal).
          Canal sem pull de gasto mostra “indisponível”, nunca zero.
        </Text>
        {isLoading && <Text className="mt-3">carregando…</Text>}
        {!isLoading && !errMsg && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-2 pr-4 font-medium">Canal</th>
                  <th className="py-2 pr-4 font-medium">Gasto</th>
                  <th className="py-2 pr-4 font-medium">Novas orgs</th>
                  <th className="py-2 pr-4 font-medium">Novas pagantes</th>
                  <th className="py-2 pr-4 font-medium">Receita</th>
                  <th className="py-2 pr-4 font-medium">CAC</th>
                  <th className="py-2 pr-4 font-medium">CAC-pago</th>
                  <th className="py-2 pr-4 font-medium">Payback (×LTV)</th>
                  <th className="py-2 pr-4 font-medium">ROAS</th>
                  <th className="py-2 pr-4 font-medium">Conversão</th>
                </tr>
              </thead>
              <tbody>
                {canais.map((c) => (
                  <tr key={c.canal} className="border-b border-slate-800/50 text-slate-200">
                    <td className="py-2 pr-4">
                      <Badge color={canalColor(c.canal)} size="xs">
                        {c.canal}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4">
                      {c.spend === null ? (
                        <span className="text-xs text-amber-400" title={c.spend_status}>
                          indisponível
                        </span>
                      ) : (
                        fmtBRL(c.spend)
                      )}
                    </td>
                    <td className="py-2 pr-4">{fmtInt(c.novas_orgs)}</td>
                    <td className="py-2 pr-4">{fmtInt(c.novas_orgs_pagantes)}</td>
                    <td className="py-2 pr-4">{fmtBRL(c.receita)}</td>
                    <td className="py-2 pr-4">{fmtBRL(c.cac)}</td>
                    <td className="py-2 pr-4 font-medium">{fmtBRL(c.cac_pago)}</td>
                    <td className="py-2 pr-4">{fmtX(c.payback_cac_sobre_ltv)}</td>
                    <td className="py-2 pr-4">{c.roas === null ? '—' : `${c.roas.toFixed(2)}×`}</td>
                    <td className="py-2 pr-4">{fmtPct(c.conversao_pagante)}</td>
                  </tr>
                ))}
                {canais.length === 0 && (
                  <tr>
                    <td colSpan={10} className="py-4 text-center text-slate-500">
                      sem dados no período
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
